import React, { useState } from "react";
import { Form, Input, Card, Typography } from "antd";
import { useNavigate } from "react-router-dom";
import UserServ from "../service/UserServ";
import Navbar from "./Navbar";
import GlobalButton from "../util/GlobalButton";
import Notify from "../util/Notify";
import "../css/loginAndSignUp.css";

const { Text } = Typography;

const SellerRequestForm = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [userInfo, setUserInfo] = useState(null);

  // Get user info from token
  React.useEffect(() => {
    const token = localStorage.getItem("token");
    const user = localStorage.getItem("user");

    if (!token) {
      // Not logged in - redirect to login
      Notify.warning("Continue Shopping", "Please log in to apply as a seller.");
      setTimeout(() => navigate("/login"), 2000);
      return;
    }

    if (user) {
      setUserInfo(JSON.parse(user));
    }
  }, [navigate]);

  const onFinish = async (values) => {
    try {
      setLoading(true);

      const payload = {
        shopName: values.shopName,
        shopDescription: values.shopDescription || "",
      };

      // Submit seller request
      await UserServ.requestSeller(payload);

      Notify.success(
        "Application Submitted!",
        "Your seller application has been submitted successfully. Our team will review it and notify you within 24-48 hours."
      );

      // Clear form
      form.resetFields();

      // Redirect after success
      setTimeout(() => {
        navigate("/", { state: { message: "Application submitted!" } });
      }, 2500);
    } catch (error) {
      console.error("Seller request error:", error);

      // Check if it's a "pending request already exists" error
      // Backend returns 409 for this specific case
      if (error?.status === 409 || error?.message?.includes("pending")) {
        Notify.warning(
          "Pending Request Exists",
          error?.response?.data?.message || error?.message || "You already have a pending seller request. Please wait for admin review."
        );
        return;
      }

      Notify.error(error, "Failed to submit application. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!userInfo) {
    return (
      <>
        <Navbar />
        <div className="login-container" style={{ height: "60vh" }}>
          <div style={{ textAlign: "center", marginTop: "80px" }}>
            <Text>Loading...</Text>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="login-container">
        <Card
          title="Apply to Become a Seller"
          bordered={false}
          className="login-card"
        >
          {/* User Info Display */}
          <div
            style={{
              backgroundColor: "#e6f7ff",
              padding: "16px",
              borderRadius: "8px",
              marginBottom: "24px",
              borderLeft: "4px solid #1890ff",
            }}
          >
            <Text strong>Logged in as:</Text>
            <div style={{ marginTop: "8px", color: "#0050b3" }}>
              <Text strong>{userInfo?.firstName} {userInfo?.lastName}</Text>
              <br />
              <Text>{userInfo?.email}</Text>
            </div>
          </div>

          <Form
            form={form}
            name="seller-request"
            layout="vertical"
            onFinish={onFinish}
            autoComplete="off"
          >
            {/* Shop Name */}
            <Form.Item
              label="Shop Name"
              name="shopName"
              rules={[
                { required: true, message: "Please enter your shop name!" },
                {
                  min: 3,
                  message: "Shop name must be at least 3 characters!",
                },
                {
                  max: 100,
                  message: "Shop name must not exceed 100 characters!",
                },
              ]}
            >
              <Input
                className="form-input"
                placeholder="E.g., John's Electronics Store"
                disabled={loading}
                maxLength={100}
              />
            </Form.Item>

            {/* Shop Description */}
            <Form.Item
              label="Shop Description"
              name="shopDescription"
              rules={[
                {
                  min: 10,
                  message: "Description must be at least 10 characters!",
                },
                {
                  max: 500,
                  message: "Description must not exceed 500 characters!",
                },
              ]}
            >
              <Input.TextArea
                className="form-input"
                placeholder="Tell us about your shop, what you sell, and your business experience..."
                rows={4}
                disabled={loading}
                maxLength={500}
                showCount
              />
            </Form.Item>

            {/* Info Message */}
            <div
              style={{
                backgroundColor: "#fffbe6",
                padding: "12px",
                borderRadius: "4px",
                marginBottom: "16px",
                borderLeft: "4px solid #faad14",
              }}
            >
              <Text style={{ color: "#7d6e00", fontSize: "13px" }}>
                ℹ️ Your application will be reviewed by our team within 24-48 hours.
                You'll receive a notification once approved.
              </Text>
            </div>

            {/* Submit Button */}
            <Form.Item>
              <GlobalButton
                action="apply"
                htmlType="submit"
                block
                loading={loading}
              >
                Submit Application
              </GlobalButton>
            </Form.Item>

            {/* Back Link */}
            <div style={{ textAlign: "center" }}>
              <Text>
                <a href="/start-selling" style={{ color: "#1890ff", fontWeight: "500" }}>
                  ← Back
                </a>
              </Text>
            </div>
          </Form>
        </Card>
      </div>
    </>
  );
};

export default SellerRequestForm;
